import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  FormControl,
  Grid,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  TextField,
  Typography,
  Chip,
  Stack,
} from "@mui/material";
import React, {useState, useContext, useEffect, useRef} from "react";
import CustomButton from "../../common/Button/CustomButton";
import { Paper, Divider, Box } from "@mui/material";
import Tooltip from "@mui/material/Tooltip";
import DeleteIcon from "@mui/icons-material/Delete";
import IconButton from "@mui/material/IconButton";
import Checkbox from "@mui/material/Checkbox";
import "./styles.scss";
import Loader from "../../loader/Loader";
import OrderSummary from "../../app/PlaceOrder/OrderSummary";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Grow from "@mui/material/Grow";
import { formatDateTimeForAdmin } from "../../../utils/dateUtils";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";

const ViewOrder = ({detailedOrder, hideModal, onSetOrder}) => {
  const [initOrder, setInitOrder] = useState({});
  const [loading, setLoading] = useState(true);
  const [formError, setFormError] = useState("");
  const courierPartnerRef = useRef(null);
  const courierTrackingUrlRef = useRef(null);
  const courierTrackingIdRef = useRef(null);
  //console.log("detailedOrder:", detailedOrder);

  const titleInputLimit = {
    minLength: 5,
    maxLength: 200,
  };
  useEffect(() => {
    if (initOrder.id === undefined) {
      setInitOrder({...detailedOrder});
    }
  }, [detailedOrder]);
  useEffect(() => {
    if (initOrder.id !== undefined) {
      setLoading(false);
    }
  }, [initOrder]);

  const handleChangeOrderDetail = (updateData) => {
    const _initOrder = {
      ...initOrder,
      ...updateData,
    };
    setInitOrder({..._initOrder});
  };
  const onClickUpdate = () => {
    console.log("init order now:", initOrder);
    let dataToSend = initOrder;
    if (initOrder.status === "Shipped") {
      if (courierPartnerRef.current.value.length < 2) {
        setFormError("Please enter courier partner.");
        courierPartnerRef.current.focus();
        return;
      }
      if (courierTrackingUrlRef.current.value.length < 2) {
        setFormError("Please enter courier tracking url.");
        courierTrackingUrlRef.current.focus();
        return;
      }
      if (courierTrackingIdRef.current.value.length < 2) {
        setFormError("Please enter courier tracking id.");
        courierTrackingIdRef.current.focus();
        return;
      }
      dataToSend = {
        ...initOrder,
        shippedAt: new Date(),
      };
    }
    if (initOrder.status === "Delivered") {
      dataToSend = {
        ...initOrder,
        deliveredAt: new Date(),
      };
    }
    if (initOrder.status === "Out for delivery") {
      dataToSend = {
        ...initOrder,
        ofdAt: new Date(),
      };
    }
    if (
      initOrder.status === "Cancelled by buyer" ||
      initOrder.status === "Cancelled by seller"
    ) {
      dataToSend = {
        ...initOrder,
        cancelledAt: new Date(),
      };
    }
    if (initOrder.status === "Refunded") {
      dataToSend = {
        ...initOrder,
        refundedAt: new Date(),
      };
    }
    onSetOrder(dataToSend);
  };
  // Status chip helper
  const statusChip = (() => {
    const s = (initOrder?.status || "").toLowerCase();
    if (s.includes("placed")) return { label: "Placed", color: "info", variant: "filled" };
    if (s.includes("shipped")) return { label: "Shipped", color: "primary", variant: "filled" };
    if (s.includes("out for delivery")) return { label: "Out for Delivery", color: "primary", variant: "filled" };
    if (s.includes("delivered")) return { label: "Delivered", color: "success", variant: "filled" };
    if (s.includes("cancelled")) return { label: "Cancelled", color: "default", variant: "outlined" };
    if (s.includes("refunded")) return { label: "Refunded", color: "warning", variant: "filled" };
    return { label: initOrder?.status || "N/A", color: "default", variant: "outlined" };
  })();
  // Payment status chip color helper
  const paymentChipColor = (status) => {
    const t = (status || "").toLowerCase();
    if (t.includes("captured") || t.includes("success")) return "success";
    if (t.includes("authorized")) return "info";
    if (t.includes("failed") || t.includes("error")) return "error";
    if (t.includes("pending") || t.includes("attempt")) return "warning";
    return "default";
  };
  // Layout helpers
  const Section = ({ title, children, right }) => (
    <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{title}</Typography>
        {right || null}
      </Stack>
      <Divider sx={{ mb: 1 }} />
      <Box>{children}</Box>
    </Paper>
  );
  const DetailRow = ({ label, children }) => (
    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
      <Typography variant="body2" sx={{ fontWeight: 500, minWidth: 120 }}>{label}:</Typography>
      <Box sx={{ wordBreak: "break-word" }}>{children}</Box>
    </Stack>
  );
  return (
    <Grid container spacing={2}>
      {loading && (
        <Grid item xs={12}>
          <Loader type="CIRCULAR" />
        </Grid>
      )}
      {!loading && (
        <>
          <Grid item xs={12}>
            <Typography variant="body2" sx={{color: "darkred"}}>
              {formError}
            </Typography>
          </Grid>
          {/* Enhanced Header */}
          <Grid item xs={12}>
            <Paper elevation={0} sx={{ p: 2, mb: 1, borderRadius: 2, border: "1px solid", borderColor: "divider" }}>
              <Grid container alignItems="center" spacing={2}>
                <Grid item xs={12} md={8}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>Order #{initOrder.id}</Typography>
                    <Tooltip title="Copy Order ID">
                      <IconButton size="small" aria-label="copy-order-id" onClick={() => navigator.clipboard?.writeText?.(initOrder.id)}>
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                  <Typography variant="body2" color="text.secondary">
                    Created {formatDateTimeForAdmin(initOrder?.createdAt)}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4} sx={{ textAlign: { xs: "left", md: "right" } }}>
                  <Chip label={statusChip.label} color={statusChip.color} variant={statusChip.variant} sx={{ fontWeight: 600 }} />
                </Grid>
              </Grid>
            </Paper>
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              id="orderId"
              label="Order Id"
              variant="standard"
              value={initOrder.id}
              aria-readonly
            />
          </Grid>
          <Grid item xs={6}>
            <FormControl fullWidth>
              <InputLabel id="status">Select status</InputLabel>
              <Select
                labelId="status"
                id="status"
                value={initOrder.status}
                label="Select status"
                onChange={(e) =>
                  handleChangeOrderDetail({
                    status: e.target.value,
                  })
                }
                size="medium"
              >
                <MenuItem value="Placed">Placed</MenuItem>
                <MenuItem value="Shipped">Shipped</MenuItem>
                <MenuItem value="Out for delivery">Out for delivery</MenuItem>
                <MenuItem value="Delivered">Delivered</MenuItem>
                <MenuItem value="Cancelled by buyer">
                  Cancelled by buyer
                </MenuItem>
                <MenuItem value="Cancelled by seller">
                  Cancelled by seller
                </MenuItem>
                <MenuItem value="Refunded">Refunded</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid
            item
            xs={12}
            sm={4}
            sx={{display: initOrder.status === "Shipped" ? "flex" : "none"}}
          >
            <Grow in={initOrder.status === "Shipped"}>
              <TextField
                inputRef={courierPartnerRef}
                fullWidth
                id="courier-partner"
                label="Courier Partner"
                variant="standard"
                value={
                  initOrder.courierDetails && initOrder.courierDetails.partner
                    ? initOrder.courierDetails.partner
                    : ""
                }
                onChange={(e) =>
                  handleChangeOrderDetail({
                    courierDetails: {
                      ...initOrder.courierDetails,
                      partner: e.target.value,
                    },
                  })
                }
                inputProps={titleInputLimit}
              />
            </Grow>
          </Grid>
          <Grid
            item
            xs={12}
            sm={4}
            sx={{display: initOrder.status === "Shipped" ? "flex" : "none"}}
          >
            <Grow
              in={initOrder.status === "Shipped"}
              style={{transformOrigin: "0 0 0"}}
              {...(initOrder.status === "Shipped" ? {timeout: 500} : {})}
            >
              <TextField
                inputRef={courierTrackingUrlRef}
                fullWidth
                id="tracking-url"
                label="Tracking Url"
                variant="standard"
                value={
                  initOrder.courierDetails &&
                  initOrder.courierDetails.trackingUrl
                    ? initOrder.courierDetails.trackingUrl
                    : ""
                }
                onChange={(e) =>
                  handleChangeOrderDetail({
                    courierDetails: {
                      ...initOrder.courierDetails,
                      trackingUrl: e.target.value,
                    },
                  })
                }
                inputProps={titleInputLimit}
              />
            </Grow>
          </Grid>
          <Grid
            item
            xs={12}
            sm={4}
            sx={{display: initOrder.status === "Shipped" ? "flex" : "none"}}
          >
            <Grow
              in={initOrder.status === "Shipped"}
              style={{transformOrigin: "0 0 0"}}
              {...(initOrder.status === "Shipped" ? {timeout: 1000} : {})}
            >
              <TextField
                inputRef={courierTrackingIdRef}
                fullWidth
                id="tracking-id"
                label="Tracking Id"
                variant="standard"
                value={
                  initOrder.courierDetails &&
                  initOrder.courierDetails.trackingId
                    ? initOrder.courierDetails.trackingId
                    : ""
                }
                onChange={(e) =>
                  handleChangeOrderDetail({
                    courierDetails: {
                      ...initOrder.courierDetails,
                      trackingId: e.target.value,
                    },
                  })
                }
                inputProps={titleInputLimit}
              />
            </Grow>
          </Grid>
          {/* Order Summary */}
          <Grid item xs={12}>
            <Accordion>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="order-content"
                id="order-header"
              >
                Order Summary (
                {initOrder.items.length > 1 ? (
                  <span>{initOrder.items.length} different products</span>
                ) : (
                  <span>1 Product</span>
                )}{" "}
                )
              </AccordionSummary>
              <AccordionDetails>
                <OrderSummary
                  items={initOrder.items}
                  max_width="md"
                  no_wrap={false}
                  order={initOrder}
                />
              </AccordionDetails>
            </Accordion>
          </Grid>
          {/* Payment Details */}
          <Grid item xs={12}>
            <Accordion>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="order-content"
                id="order-header"
              >
                Payment Details ({initOrder.payment.method})
              </AccordionSummary>
              <AccordionDetails>
                {initOrder.payment.method !== "cod" && (
                  <>
                    <label style={{fontWeight: 500}}>Method: </label>
                    {initOrder.payment.method}
                    <Section title="Payment">
                      <DetailRow label="Status">
                        <Chip size="small" label={initOrder.payment.status} color={paymentChipColor(initOrder.payment.status)} variant="outlined" />
                      </DetailRow>
                      <DetailRow label="Payment Id">
                        <Typography component="span" variant="body2" sx={{ fontFamily: "monospace" }}>{initOrder.payment.payment_id}</Typography>
                      </DetailRow>
                      <DetailRow label="Attempted At">
                        {formatDateTimeForAdmin(initOrder?.payment?.attempted_at)}
                      </DetailRow>
                    </Section>
                  </>
                )}
                {initOrder.payment.method === "cod" && (
                  <>
                    <label style={{fontWeight: 500}}>Method: </label>
                    Cash on delivery
                    <DetailRow label="Status">
                       <Chip size="small" label={initOrder.payment.status} color={paymentChipColor(initOrder.payment.status)} variant="outlined" />
                     </DetailRow>
                  </>
                )}
              </AccordionDetails>
            </Accordion>
          </Grid>
          {/* Buyer Details */}
          <Grid item xs={12}>
            <Accordion>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="buyer-content"
                id="buyer-header"
              >
                Buyer Detail
              </AccordionSummary>
              <AccordionDetails>
                <>
                  <Section title="Account">
                    <DetailRow label="User Id">
                      <Typography component="span" variant="body2" sx={{ fontFamily: "monospace" }}>{initOrder?.user?.id}</Typography>
                    </DetailRow>
                    <DetailRow label="Name">{initOrder?.user?.name}</DetailRow>
                    <DetailRow label="Email">{initOrder?.user?.email}</DetailRow>
                  </Section>
                  <Section title="Shipping Address">
                    <DetailRow label="Name">{initOrder?.address?.name}</DetailRow>
                    <DetailRow label="Mobile">
                      <Typography component="span">{initOrder?.address?.mobile}</Typography>
                      {initOrder?.address?.alternateMobile !== "" && (
                        <Typography component="span" sx={{ ml: 0.5 }}>, {initOrder?.address?.alternateMobile}</Typography>
                      )}
                    </DetailRow>
                    <DetailRow label="Address">
                      <Typography component="span">
                        {initOrder?.address?.address}
                        {initOrder?.address?.landmark !== "" && (
                          <Typography component="span">, Landmark: {initOrder?.address?.landmark}</Typography>
                        )}
                        , {initOrder?.address?.city}, {initOrder?.address?.state}-{initOrder?.address?.pincode}
                      </Typography>
                    </DetailRow>
                  </Section>
                </>
              </AccordionDetails>
            </Accordion>
          </Grid>
          {/* Shipment Details */}
          <Grid item xs={12}>
            <Accordion>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="buyer-content"
                id="buyer-header"
              >
                Shipment Detail
              </AccordionSummary>
              <AccordionDetails>
                <>
                  <h4 className="buyer-details-h">
                    Courier Partner:{" "}
                    {initOrder.courierDetails !== undefined
                      ? initOrder.courierDetails.partner
                      : ""}
                  </h4>
                  <p className="buyer-details-p">
                    Tracking Id:{" "}
                    {initOrder.courierDetails !== undefined
                      ? initOrder.courierDetails.trackingId
                      : ""}
                  </p>
                  <p className="buyer-details-p">
                    Tracking URL:{" "}
                    {initOrder.courierDetails !== undefined ? (
                      <a
                        href={initOrder.courierDetails.trackingUrl}
                        target="blank"
                      >
                        {initOrder.courierDetails.trackingUrl}
                      </a>
                    ) : (
                      ""
                    )}
                  </p>
                  <h4 className="buyer-details-h">Shipping address</h4>
                  <p className="buyer-details-p">{initOrder.address.name}</p>
                  <p className="buyer-details-p">
                    {initOrder.address.mobile}
                    {initOrder.address.alternateMobile !== "" && (
                      <span>, {initOrder.address.alternateMobile}</span>
                    )}
                  </p>
                  <p className="buyer-details-p">
                    {initOrder.address.address}
                    {initOrder.address.landmark !== "" && (
                      <span>, Landmark: {initOrder.address.landmark}</span>
                    )}
                    , {initOrder.address.city}, {initOrder.address.state}-
                    {initOrder.address.pincode}
                  </p>
                </>
              </AccordionDetails>
            </Accordion>
          </Grid>
          {/* Buttons */}
          <Grid item xs={6}>
            <CustomButton
              variant="outlined"
              color="primary"
              size="small"
              onClick={hideModal}
              fullWidth
              sx={{marginTop: "15px"}}
            >
              Cancel
            </CustomButton>
          </Grid>
          <Grid item xs={6}>
            <CustomButton
              variant="contained"
              color="primary"
              size="small"
              onClick={onClickUpdate}
              fullWidth
              sx={{marginTop: "15px"}}
            >
              Update Status
            </CustomButton>
          </Grid>
        </>
      )}
    </Grid>
  );
};

export default ViewOrder;
